// import React, { useState, useEffect, useRef } from "react";
// import { useParams, useLocation } from "react-router-dom";
// import LeftSidebar from "./LeftSidebar";
// import ChatPanel from "./ChatPanel";
// import RightPanel from "./RightPanel";
// import SearchOverlay from "./SearchOverlay";
// import CreateChannelModal from "../../channel/components/CreateChannelModal";
// import MobileBottomNav from "./MobileBottomNav";
// import AppLogo from "../../../components/ui/AppLogo";
// import { useChat } from "../../../context/ChatContext";
// import { useNotifPermission } from "../../../context/SocketContext";
// import ChannelSettingsPanel from "../../channel/components/ChannelSettingsPanel";
// import DraftsModal from "./DraftsModal";
// import UserProfileModal from "./UserProfileModal";
// import AppIcon from "../../../components/ui/AppIcon";

// export default function ChatInterface() {
//   const {
//     activeChat,
//     channels,
//     directMessages,
//     selectChat,
//     showDraftsModal,
//     setShowDraftsModal,
//     setTargetMessageId,
//     showUserProfileModal,
//     setShowUserProfileModal,
//     selectedProfileUserId,
//   } = useChat();
//   const { permissionState, requestPermission } = useNotifPermission();
//   const [bannerDismissed, setBannerDismissed] = useState(false);
//   const { id } = useParams();
//   const location = useLocation();

//   // ── URL Sync Logic ────────────────────────────────────────────────────────
//   // Read messageId from URL query params for exact message redirect
//   useEffect(() => {
//     const searchParams = new URLSearchParams(location.search);
//     const msgId = searchParams.get("messageId");
//     if (msgId) {
//       console.log(
//         "[ChatInterface] Found messageId in URL, setting targetMessageId:",
//         msgId,
//       );
//       setTargetMessageId(msgId);
//     }
//   }, [location.search, setTargetMessageId]);

//   // IMPORTANT: activeChat is intentionally NOT in the deps array.
//   // Including it causes a feedback loop: SearchOverlay calls selectChat() to
//   // switch to a new chat, which changes activeChat, which re-runs this effect,
//   // which sees id !== activeChat.data._id and calls selectChat() back to the
//   // URL chat — undoing the search navigation.
//   // We use a ref to read the current activeChat without adding it to deps.
//   const activeChatRef = useRef(activeChat);
//   useEffect(() => {
//     activeChatRef.current = activeChat;
//   }, [activeChat]);

//   useEffect(() => {
//     if (!id) return;

//     const isChannel = location.pathname.startsWith("/channel/");
//     const isDM = location.pathname.startsWith("/dm/");

//     // Only sync if the URL id doesn't already match the current activeChat.
//     // Reading via ref avoids adding activeChat to deps (which causes the loop).
//     const currentActiveChatId =
//       activeChatRef.current?.data?._id || activeChatRef.current?.data?.id;
//     if (currentActiveChatId === id) return;

//     if (isChannel && channels.length > 0) {
//       const found = channels.find((c) => (c._id || c.id) === id);
//       if (found) selectChat("channel", found, true);
//     } else if (isDM && directMessages.length > 0) {
//       const found = directMessages.find((d) => (d._id || d.id) === id);
//       if (found) selectChat("dm", found, true);
//     }
//   }, [id, location.pathname, channels, directMessages, selectChat]);

//   const [searchOpen, setSearchOpen] = useState(false);
//   const [createChannelOpen, setCreateChannelOpen] = useState(false);
//   const [rightPanelOpen, setRightPanelOpen] = useState(true);
//   const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer
//   const [showSettings, setShowSettings] = useState(false);
//   const [mobileView, setMobileView] = useState("chat");

//   // Close right panel on ESC key (mobile drawer behaviour)
//   useEffect(() => {
//     const handleKeyDown = (e) => {
//       if (e.key === "Escape" && rightPanelOpen) setRightPanelOpen(false);
//     };
//     document.addEventListener("keydown", handleKeyDown);
//     return () => document.removeEventListener("keydown", handleKeyDown);
//   }, [rightPanelOpen]);

//   // Lock body scroll on mobile when right panel is open
//   useEffect(() => {
//     const isMobile = window.innerWidth < 768;
//     if (isMobile && rightPanelOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "";
//     }
//     return () => {
//       document.body.style.overflow = "";
//     };
//   }, [rightPanelOpen]);

//   // ── Desktop sidebar toggle (persisted) ───────────────────────────────────
//   const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(() => {
//     try {
//       const stored = localStorage.getItem("desktopSidebarOpen");
//       // Default to open — only close if user explicitly closed it
//       return stored === null ? true : stored !== "false";
//     } catch {
//       return true;
//     }
//   });
//   const toggleDesktopSidebar = () => {
//     setDesktopSidebarOpen((prev) => {
//       const next = !prev;
//       try {
//         localStorage.setItem("desktopSidebarOpen", String(next));
//       } catch {}
//       return next;
//     });
//   };

//   return (
//     <div className="h-screen flex flex-col bg-app-bg text-text-primary overflow-hidden font-sans">
//       {/* NOTIFICATION PERMISSION BANNER */}
//       {!bannerDismissed && permissionState === "denied" && (
//         <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-500/10 border-b border-amber-500/20 text-amber-400 text-[12px] font-medium z-50">
//           <div className="flex items-center gap-2">
//             <AppIcon name="BellSlashIcon" size={15} />
//             <span>
//               Notifications are blocked. To enable them: click the lock icon in
//               your browser's address bar → Notifications → Allow, then refresh.
//             </span>
//           </div>
//           <button
//             onClick={() => setBannerDismissed(true)}
//             className="flex-shrink-0 p-1 rounded hover:bg-amber-500/20 transition-colors"
//             aria-label="Dismiss"
//           >
//             <AppIcon name="XMarkIcon" size={14} />
//           </button>
//         </div>
//       )}

//       {/* NOTIFICATION PERMISSION BANNER — default state (not yet asked) */}
//       {!bannerDismissed && permissionState === "default" && (
//         <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/10 border-b border-primary/20 text-primary text-[12px] font-medium z-50">
//           <div className="flex items-center gap-2">
//             <AppIcon name="BellIcon" size={15} />
//             <span>
//               Enable desktop notifications to get alerted when you receive
//               messages.
//             </span>
//           </div>
//           <div className="flex items-center gap-2 flex-shrink-0">
//             <button
//               onClick={() => requestPermission()}
//               className="px-3 py-1 rounded-lg bg-primary text-white text-[11px] font-bold hover:bg-primary-dark transition-colors"
//             >
//               Enable
//             </button>
//             <button
//               onClick={() => setBannerDismissed(true)}
//               className="p-1 rounded hover:bg-primary/20 transition-colors"
//               aria-label="Dismiss"
//             >
//               <AppIcon name="XMarkIcon" size={14} />
//             </button>
//           </div>
//         </div>
//       )}

//       {/* MOBILE HEADER */}
//       <div className="flex md:hidden items-center justify-between px-5 h-[60px] bg-app-surface/80 backdrop-blur-md border-b border-border-subtle z-50">
//         <button
//           onClick={() => setSidebarOpen(!sidebarOpen)}
//           className="p-2 -ml-2 text-text-muted hover:text-text-primary transition-colors"
//         >
//           <div className="space-y-1.5 w-5">
//             <div className="h-0.5 bg-current rounded-full" />
//             <div className="h-0.5 bg-current rounded-full w-3" />
//             <div className="h-0.5 bg-current rounded-full" />
//           </div>
//         </button>
//         <div className="flex items-center gap-2">
//           <AppLogo size={28} />
//           <span className="font-bold tracking-tight text-text-primary">
//             ChatFlow
//           </span>
//         </div>
//         <button
//           onClick={() => setSearchOpen(true)}
//           className="p-2 -mr-2 text-text-muted hover:text-text-primary transition-colors"
//         >
//           <AppIcon name="MagnifyingGlassIcon" size={20} />
//         </button>
//       </div>

//       {/* pb-[64px] on mobile reserves space for the fixed bottom nav */}
//       <main className="flex flex-1 overflow-hidden relative pb-[64px] md:pb-0">
//         {/* SIDEBAR NAVIGATION */}
//         <div
//           className={`
//                     fixed inset-0 z-40
//                     md:relative md:inset-auto md:z-0
//                     transition-all duration-300 ease-in-out transform
//                     ${
//                       sidebarOpen || mobileView === "channels"
//                         ? "translate-x-0"
//                         : "-translate-x-full md:translate-x-0"
//                     }
//                     ${
//                       desktopSidebarOpen
//                         ? "md:w-[260px] md:opacity-100"
//                         : "md:w-0 md:opacity-0 md:overflow-hidden md:pointer-events-none"
//                     }
//                 `}
//         >
//           <div className="w-[260px] h-full">
//             <LeftSidebar
//               onSearchOpen={() => setSearchOpen(true)}
//               onCreateChannel={() => setCreateChannelOpen(true)}
//             />
//           </div>
//           {/* Mobile overlay */}
//           {(sidebarOpen || mobileView === "channels") && (
//             <div
//               className="md:hidden absolute inset-0 left-full w-screen bg-black/20 backdrop-blur-sm"
//               onClick={() => {
//                 setSidebarOpen(false);
//                 setMobileView("chat");
//               }}
//             />
//           )}
//         </div>

//         {/* CENTRAL CHAT AREA */}
//         <div className="flex-1 flex flex-col min-w-0 bg-app-bg shadow-sm">
//           <ChatPanel
//             onRightPanelToggle={() => setRightPanelOpen(!rightPanelOpen)}
//             rightPanelOpen={rightPanelOpen}
//             onSidebarToggle={toggleDesktopSidebar}
//             desktopSidebarOpen={desktopSidebarOpen}
//           />
//         </div>

//         {/* RIGHT CONTEXT PANEL */}
//         {/* Mobile: fixed slide-in drawer from right; Desktop: static sidebar */}
//         <>
//           {/* Backdrop — mobile only, shown when panel is open */}
//           {rightPanelOpen && (
//             <div
//               className="md:hidden fixed inset-0 z-30 bg-black/30 backdrop-blur-sm"
//               onClick={() => setRightPanelOpen(false)}
//               aria-hidden="true"
//             />
//           )}

//           <div
//             role="dialog"
//             aria-modal="true"
//             aria-label="Details panel"
//             className={`
//                             fixed right-0 top-0 h-full z-40 w-[85vw] max-w-[320px]
//                             md:relative md:inset-auto md:z-0 md:h-auto
//                             bg-app-surface border-l border-border-subtle
//                             transition-transform duration-300 ease-in-out
//                             ${
//                               rightPanelOpen
//                                 ? "translate-x-0 md:w-[300px] lg:w-[350px]"
//                                 : "translate-x-full md:translate-x-0 md:w-0 md:overflow-hidden md:border-none"
//                             }
//                         `}
//           >
//             {showSettings && activeChat?.type === "channel" ? (
//               <ChannelSettingsPanel
//                 channel={activeChat.data}
//                 onClose={() => setShowSettings(false)}
//               />
//             ) : (
//               <RightPanel
//                 onClose={() => setRightPanelOpen(false)}
//                 onEdit={() => setShowSettings(true)}
//               />
//             )}
//           </div>
//         </>
//       </main>

//       {/* MOBILE NAVIGATION */}
//       <MobileBottomNav
//         activeView={mobileView}
//         onViewChange={(view) => {
//           setMobileView(view);
//           if (view !== "channels") setSidebarOpen(false);
//         }}
//         onSearchOpen={() => setSearchOpen(true)}
//       />

//       {/* MODALS & OVERLAYS */}
//       {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
//       {createChannelOpen && (
//         <CreateChannelModal onClose={() => setCreateChannelOpen(false)} />
//       )}
//       {showDraftsModal && (
//         <DraftsModal onClose={() => setShowDraftsModal(false)} />
//       )}
//       {showUserProfileModal && (
//         <UserProfileModal
//           userId={selectedProfileUserId}
//           onClose={() => setShowUserProfileModal(false)}
//         />
//       )}
//     </div>
//   );
// }

//================================================

import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import LeftSidebar from "./LeftSidebar";
import ChatPanel from "./ChatPanel";
import RightPanel from "./RightPanel";
import SearchOverlay from "./SearchOverlay";
import CreateChannelModal from "../../channel/components/CreateChannelModal";
import MobileBottomNav from "./MobileBottomNav";
import NotificationPermissionModal from "./NotificationPermissionModal";
import AppLogo from "../../../components/ui/AppLogo";
import { useChat } from "../../../context/ChatContext";
import { useAuth } from "../../../context/AuthContext";
import { useNotifPermission } from "../../../context/SocketContext";
import { useThread } from "../../../context/ThreadContext";
import ChannelSettingsPanel from "../../channel/components/ChannelSettingsPanel";
import DraftsModal from "./DraftsModal";
import UserProfileModal from "./UserProfileModal";
import ThreadModal from "../../thread/components/ThreadModal";
import ThreadsListModal from "../../thread/components/ThreadsListModal";
import AppIcon from "../../../components/ui/AppIcon";

export default function ChatInterface() {
  const {
    activeChat,
    channels,
    directMessages,
    selectChat,
    showDraftsModal,
    setShowDraftsModal,
    showThreadsModal,
    setShowThreadsModal,
    setTargetMessageId,
    showUserProfileModal,
    setShowUserProfileModal,
    selectedProfileUserId,
    openUserProfile,
  } = useChat();
  const { user } = useAuth();
  const { permissionState, requestPermission } = useNotifPermission();
  const { openThread, closeThreadPanel, openThreadPanel } = useThread();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const { id } = useParams();
  const location = useLocation();

  // ── Mobile notification opt-in ────────────────────────────────────────────
  // Show an explicit Allow / Remind-me-later popup on mobile after login.
  // Rules: never show once granted; otherwise show at most once per calendar
  // day until the user grants permission.
  const [showNotifModal, setShowNotifModal] = useState(false);
  const NOTIF_PROMPT_KEY = "notif_prompt_last_shown";

  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth >= 768) return;
    // Already granted (or unsupported) → never prompt.
    if (permissionState === "granted" || permissionState === "unsupported") {
      setShowNotifModal(false);
      return;
    }
    // "default" or "denied" → show once per day.
    let today = "";
    try {
      today = new Date().toISOString().slice(0, 10);
    } catch (_) {}
    let last = null;
    try {
      last = localStorage.getItem(NOTIF_PROMPT_KEY);
    } catch (_) {}
    if (last !== today) {
      setShowNotifModal(true);
      // Stamp now so reloads within the same day don't re-prompt.
      try {
        localStorage.setItem(NOTIF_PROMPT_KEY, today);
      } catch (_) {}
    }
  }, [permissionState]);

  const handleNotifAllow = async () => {
    setShowNotifModal(false);
    try {
      await requestPermission();
    } catch (_) {}
  };

  const handleNotifRemindLater = () => {
    setShowNotifModal(false);
  };

  const handleOpenThreadFromList = (parentMessage, chatId, chatType) => {
    openThreadPanel(parentMessage, chatId, chatType);
  };

  // ── URL Sync Logic ────────────────────────────────────────────────────────
  // Read messageId from URL query params for exact message redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const msgId = searchParams.get("messageId");
    if (msgId) {
      console.log(
        "[ChatInterface] Found messageId in URL, setting targetMessageId:",
        msgId,
      );
      setTargetMessageId(msgId);
    }
  }, [location.search, setTargetMessageId]);

  // IMPORTANT: activeChat is intentionally NOT in the deps array.
  // Including it causes a feedback loop: SearchOverlay calls selectChat() to
  // switch to a new chat, which changes activeChat, which re-runs this effect,
  // which sees id !== activeChat.data._id and calls selectChat() back to the
  // URL chat — undoing the search navigation.
  // We use a ref to read the current activeChat without adding it to deps.
  const activeChatRef = useRef(activeChat);
  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  useEffect(() => {
    if (!id) return;

    const isChannel = location.pathname.startsWith("/channel/");
    const isDM = location.pathname.startsWith("/dm/");

    // Only sync if the URL id doesn't already match the current activeChat.
    // Reading via ref avoids adding activeChat to deps (which causes the loop).
    const currentActiveChatId =
      activeChatRef.current?.data?._id || activeChatRef.current?.data?.id;
    if (currentActiveChatId === id) return;

    if (isChannel && channels.length > 0) {
      const found = channels.find((c) => (c._id || c.id) === id);
      if (found) selectChat("channel", found, true);
    } else if (isDM) {
      const myId = (user?._id || user?.id)?.toString();
      // Self-DM ("Notes to self"). The current user is intentionally excluded
      // from directMessages, so resolve this one straight from the authed user.
      if (myId && myId === id) {
        selectChat(
          "dm",
          {
            ...user,
            _id: myId,
            id: myId,
            name: "Notes",
            displayName: "Notes",
            isSelf: true,
          },
          true,
        );
      } else if (directMessages.length > 0) {
        const found = directMessages.find((d) => (d._id || d.id) === id);
        if (found) selectChat("dm", found, true);
      }
    }
  }, [id, location.pathname, channels, directMessages, selectChat, user]);

  const [searchOpen, setSearchOpen] = useState(false);
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  // The right "Details" panel is a slide-in drawer on mobile — keep it closed
  // Details panel is CLOSED by default on every load/refresh (desktop & mobile).
  // The user opens it on demand via the panel toggle in the header.
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer
  const [showSettings, setShowSettings] = useState(false);
  const [mobileView, setMobileView] = useState("chat");

  // ── Mobile navigation: show the conversation list until a chat is picked ──
  // On mobile, when no conversation is selected (e.g. right after login) open
  // the sidebar list so the user can pick one; once a chat is selected via the
  // URL, close the drawer and show the conversation.
  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth >= 768) return;
    if (id) {
      setSidebarOpen(false);
      setMobileView("chat");
    } else {
      setSidebarOpen(true);
    }
  }, [id]);

  // Close right panel on ESC key (mobile drawer behaviour)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && rightPanelOpen) setRightPanelOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [rightPanelOpen]);

  // Lock body scroll on mobile when right panel is open
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile && rightPanelOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [rightPanelOpen]);

  // ── Desktop sidebar toggle (persisted) ───────────────────────────────────
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("desktopSidebarOpen");
      // Default to open — only close if user explicitly closed it
      return stored === null ? true : stored !== "false";
    } catch {
      return true;
    }
  });
  const toggleDesktopSidebar = () => {
    setDesktopSidebarOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("desktopSidebarOpen", String(next));
      } catch {}
      return next;
    });
  };

  return (
    <div className="h-screen flex flex-col bg-app-bg text-text-primary overflow-hidden font-sans">
      {/* NOTIFICATION PERMISSION BANNER */}
      {!bannerDismissed && permissionState === "denied" && (
        <div className="hidden md:flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-500/10 border-b border-amber-500/20 text-amber-400 text-[12px] font-medium z-50">
          <div className="flex items-center gap-2">
            <AppIcon name="BellSlashIcon" size={15} />
            <span>
              Notifications are blocked. To enable them: click the lock icon in
              your browser's address bar → Notifications → Allow, then refresh.
            </span>
          </div>
          <button
            onClick={() => setBannerDismissed(true)}
            className="flex-shrink-0 p-1 rounded hover:bg-amber-500/20 transition-colors"
            aria-label="Dismiss"
          >
            <AppIcon name="XMarkIcon" size={14} />
          </button>
        </div>
      )}

      {/* NOTIFICATION PERMISSION BANNER — default state (not yet asked) */}
      {!bannerDismissed && permissionState === "default" && (
        <div className="hidden md:flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/10 border-b border-primary/20 text-primary text-[12px] font-medium z-50">
          <div className="flex items-center gap-2">
            <AppIcon name="BellIcon" size={15} />
            <span>
              Enable desktop notifications to get alerted when you receive
              messages.
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => requestPermission()}
              className="px-3 py-1 rounded-lg bg-primary text-white text-[11px] font-bold hover:bg-primary-dark transition-colors"
            >
              Enable
            </button>
            <button
              onClick={() => setBannerDismissed(true)}
              className="p-1 rounded hover:bg-primary/20 transition-colors"
              aria-label="Dismiss"
            >
              <AppIcon name="XMarkIcon" size={14} />
            </button>
          </div>
        </div>
      )}

      {/* MOBILE HEADER */}
      <div className="flex md:hidden items-center justify-between px-5 h-[60px] bg-app-surface/80 backdrop-blur-md border-b border-border-subtle z-50">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 -ml-2 text-text-muted hover:text-text-primary transition-colors"
        >
          <div className="space-y-1.5 w-5">
            <div className="h-0.5 bg-current rounded-full" />
            <div className="h-0.5 bg-current rounded-full w-3" />
            <div className="h-0.5 bg-current rounded-full" />
          </div>
        </button>
        <div className="flex items-center gap-2">
          <AppLogo size={28} />
          <span className="font-bold tracking-tight text-text-primary">
            Sapient Intercom
          </span>
        </div>
        <button
          onClick={() => setSearchOpen(true)}
          className="p-2 -mr-2 text-text-muted hover:text-text-primary transition-colors"
        >
          <AppIcon name="MagnifyingGlassIcon" size={20} />
        </button>
      </div>

      {/* pb-[64px] on mobile reserves space for the fixed bottom nav */}
      <main className="flex flex-1 overflow-hidden relative pb-[64px] md:pb-0">
        {/* SIDEBAR NAVIGATION */}
        {/* Mobile backdrop — sits above the header so the drawer reads as a full overlay */}
        {(sidebarOpen || mobileView === "channels") && (
          <div
            className="md:hidden fixed inset-0 z-[55] bg-black/40 backdrop-blur-sm"
            onClick={() => {
              setSidebarOpen(false);
              setMobileView("chat");
            }}
            aria-hidden="true"
          />
        )}
        <div
          className={`
                    fixed top-0 left-0 bottom-0 z-[60] w-[82vw] max-w-[300px]
                    md:relative md:inset-auto md:z-0
                    transition-all duration-300 ease-in-out transform
                    ${
                      sidebarOpen || mobileView === "channels"
                        ? "translate-x-0"
                        : "-translate-x-full md:translate-x-0"
                    }
                    ${
                      desktopSidebarOpen
                        ? "md:w-[260px] md:opacity-100"
                        : "md:w-0 md:opacity-0 md:overflow-hidden md:pointer-events-none"
                    }
                `}
        >
          <div className="w-full md:w-[260px] h-full">
            <LeftSidebar
              onSearchOpen={() => setSearchOpen(true)}
              onCreateChannel={() => setCreateChannelOpen(true)}
            />
          </div>
        </div>

        {/* CENTRAL CHAT AREA */}
        <div className="flex-1 flex flex-col min-w-0 bg-app-bg shadow-sm">
          <ChatPanel
            onRightPanelToggle={() => setRightPanelOpen(!rightPanelOpen)}
            rightPanelOpen={rightPanelOpen}
            onSidebarToggle={toggleDesktopSidebar}
            desktopSidebarOpen={desktopSidebarOpen}
          />
        </div>

        {/* RIGHT CONTEXT PANEL */}
        {/* Mobile: fixed slide-in drawer from right; Desktop: static sidebar */}
        <>
          {/* Backdrop — mobile only, shown when panel is open */}
          {rightPanelOpen && (
            <div
              className="md:hidden fixed inset-0 z-[55] bg-black/30 backdrop-blur-sm"
              onClick={() => setRightPanelOpen(false)}
              aria-hidden="true"
            />
          )}

          <div
            role="dialog"
            aria-modal="true"
            aria-label="Details panel"
            className={`
                            fixed right-0 top-0 h-full z-[60] w-[85vw] max-w-[320px]
                            md:relative md:inset-auto md:z-0 md:h-auto
                            bg-app-surface border-l border-border-subtle
                            transition-transform duration-300 ease-in-out
                            ${
                              rightPanelOpen
                                ? "translate-x-0 md:w-[300px] lg:w-[350px]"
                                : "translate-x-full md:translate-x-0 md:w-0 md:overflow-hidden md:border-none"
                            }
                        `}
          >
            {showSettings && activeChat?.type === "channel" ? (
              <ChannelSettingsPanel
                channel={activeChat.data}
                onClose={() => setShowSettings(false)}
              />
            ) : (
              <RightPanel
                onClose={() => setRightPanelOpen(false)}
                onEdit={() => setShowSettings(true)}
              />
            )}
          </div>
        </>
      </main>

      {/* MOBILE NAVIGATION */}
      <MobileBottomNav
        activeView={mobileView}
        onViewChange={(view) => {
          // Profile is a modal action (like Search), not a persistent view —
          // open the current user's profile and keep the active tab unchanged.
          if (view === "profile") {
            openUserProfile(user?._id || user?.id);
            return;
          }
          setMobileView(view);
          if (view !== "channels") setSidebarOpen(false);
        }}
        onSearchOpen={() => setSearchOpen(true)}
      />

      {/* MODALS & OVERLAYS */}
      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
      {createChannelOpen && (
        <CreateChannelModal onClose={() => setCreateChannelOpen(false)} />
      )}
      {showDraftsModal && (
        <DraftsModal onClose={() => setShowDraftsModal(false)} />
      )}
      {showUserProfileModal && (
        <UserProfileModal
          userId={selectedProfileUserId}
          onClose={() => setShowUserProfileModal(false)}
        />
      )}

      {/* Mobile notification opt-in popup */}
      {showNotifModal && (
        <NotificationPermissionModal
          denied={permissionState === "denied"}
          onAllow={handleNotifAllow}
          onRemindLater={handleNotifRemindLater}
        />
      )}

      {/* Thread Modal - Premium Slack-style */}
      {openThread && <ThreadModal onClose={closeThreadPanel} />}

      {/* Threads List Modal - Left panel threads */}
      {showThreadsModal && (
        <ThreadsListModal
          onClose={() => setShowThreadsModal(false)}
          onOpenThread={handleOpenThreadFromList}
        />
      )}
    </div>
  );
}
